// ABOUTME: Prepara la selfie antes de enviarla: orientación EXIF, espejo, aire por encima de la
// ABOUTME: cabeza si hace falta, recorte al formato de Gemini y reducción a JPEG de 1024 px.
import { cropToAspect, nearestAspect } from "./aspect";
import { headroomPadding, type Padding } from "./frame";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PreparedSelfie {
  base64: string;
  /** Vista previa: la selfie tal cual (sin el margen de aire), recortada al formato. */
  dataUrl: string;
  aspect: string;
  /** Zona de la imagen final que viene de la selfie real; fuera de ella hay relleno. */
  originalRect: Rect;
  padded: boolean;
}

export interface Segmenter {
  segment(source: HTMLCanvasElement, timestampMs: number): { data: Uint8Array; width: number; height: number };
}

function canvasOf(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D no disponible");
  return [c, ctx];
}

/**
 * Añade margen arriba y a los lados con un color liso (el tono medio de los bordes de la
 * foto). Nada de copias desenfocadas: el modelo las interpretaba como otra persona.
 */
function padWithFlat(src: HTMLCanvasElement, pad: Padding): { canvas: HTMLCanvasElement; rect: Rect } {
  const side = Math.round(src.width * pad.sides);
  const top = Math.round(src.height * pad.top);
  const sctx = src.getContext("2d")!;
  const band = sctx.getImageData(0, 0, src.width, Math.max(1, Math.round(src.height * 0.05))).data;
  let r = 0;
  let g = 0;
  let b = 0;
  const n = band.length / 4;
  for (let i = 0; i < band.length; i += 4) {
    r += band[i];
    g += band[i + 1];
    b += band[i + 2];
  }
  const [out, ctx] = canvasOf(src.width + side * 2, src.height + top);
  ctx.fillStyle = `rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(src, side, top);
  return { canvas: out, rect: { x: side, y: top, width: src.width, height: src.height } };
}

export async function prepareSelfie(
  file: File,
  options: { maxSide?: number; quality?: number; mirror?: boolean; segmenter?: Segmenter | null } = {},
): Promise<PreparedSelfie> {
  const { maxSide = 1024, quality = 0.88, mirror = false, segmenter = null } = options;
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  // 1. Orientada y espejada, a tamaño de trabajo.
  const workScale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  let [work, wctx] = canvasOf(Math.round(bitmap.width * workScale), Math.round(bitmap.height * workScale));
  if (mirror) {
    wctx.translate(work.width, 0);
    wctx.scale(-1, 1);
  }
  wctx.drawImage(bitmap, 0, 0, work.width, work.height);
  bitmap.close();

  // Vista previa sin aire: lo que la persona ve es su selfie, no el relleno.
  const previewAspect = nearestAspect(work.width, work.height);
  const previewCrop = cropToAspect(work.width, work.height, previewAspect);
  const previewScale = Math.min(1, maxSide / Math.max(previewCrop.width, previewCrop.height));
  const [preview, pctx] = canvasOf(Math.round(previewCrop.width * previewScale), Math.round(previewCrop.height * previewScale));
  pctx.drawImage(work, previewCrop.x, previewCrop.y, previewCrop.width, previewCrop.height, 0, 0, preview.width, preview.height);
  const previewDataUrl = preview.toDataURL("image/jpeg", quality);

  // 2. Aire para el peinado si la cabeza toca arriba o llena el encuadre.
  let rect: Rect = { x: 0, y: 0, width: work.width, height: work.height };
  let padded = false;
  if (segmenter) {
    try {
      const m = segmenter.segment(work, performance.now());
      const pad = headroomPadding(m.data, m.width, m.height);
      if (pad) {
        const r = padWithFlat(work, pad);
        work = r.canvas;
        rect = r.rect;
        padded = true;
      }
    } catch (err) {
      console.warn("No se pudo analizar el encuadre:", (err as Error).message);
    }
  }

  // 3. Recorte al formato de Gemini y reducción.
  const aspect = nearestAspect(work.width, work.height);
  const crop = cropToAspect(work.width, work.height, aspect);
  const scale = Math.min(1, maxSide / Math.max(crop.width, crop.height));
  const [out, octx] = canvasOf(Math.round(crop.width * scale), Math.round(crop.height * scale));
  octx.drawImage(work, crop.x, crop.y, crop.width, crop.height, 0, 0, out.width, out.height);
  const originalRect: Rect = {
    x: Math.round((rect.x - crop.x) * scale),
    y: Math.round((rect.y - crop.y) * scale),
    width: Math.round(rect.width * scale),
    height: Math.round(rect.height * scale),
  };
  const dataUrl = out.toDataURL("image/jpeg", quality);
  // Sin aire, la vista previa y lo enviado son la misma imagen (la composición lo necesita).
  return { base64: dataUrl.split(",")[1], dataUrl: padded ? previewDataUrl : dataUrl, aspect: aspect.label, originalRect, padded };
}
